// External Dependencies
import { Form, useFormikContext } from 'formik';
import Box from '@mui/material/Box';
import { CreateInvoicePaymentRequest } from '@presto-assistant/api_types/api/admin/invoicePayments';
import InputAdornment from '@mui/material/InputAdornment';
import { CreateDocumentUploadRequest } from '@presto-assistant/api_types/api/v1/documentUpload';
import { useCallback, useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone';

// Internal Dependencies
import {
  CustomInput, SaveButton, UploadDropzone,
} from 'components/shared';
import { useCreateDocumentUpload } from 'hooks/api/documentUploads';
import { prestoApiClient } from 'utils/api/apiClient';

// Local Typings
interface InvoicePaymentFormFieldsProps {
  invoiceId: string;
  isSubmitting: boolean;
}

// Component Definition
const InvoicePaymentFormFields = ({
  invoiceId,
  isSubmitting,
}: InvoicePaymentFormFieldsProps): JSX.Element => {
  const [checkFile, setCheckFile] = useState<File | null>(null);
  const [hasUploadError, setHasUploadError] = useState(false);

  const formikContext = useFormikContext<CreateInvoicePaymentRequest['body']>();

  const {
    data: createDocumentUploadData,
    mutate: createDocumentUpload,
  } = useCreateDocumentUpload();

  useEffect(() => {
    formikContext.setFieldValue('checkImageDocumentId', createDocumentUploadData?.data.data.documentUpload.id);
  }, [createDocumentUploadData]);

  const handleUploadFile = useCallback(async (droppedFiles: FileWithPath[]) => {
    const file = droppedFiles[0];

    setCheckFile(file);

    try {
      setHasUploadError(false);

      const {
        data: {
          data: {
            getUrl,
            putUrl,
          },
        },
      } = await prestoApiClient.v1.documentUploadClient.getPresignedUrls({
        filename: droppedFiles[0].name,
        idRef: invoiceId,
        tableRef: 'invoice_payments',
      });

      const s3Response = await fetch(putUrl, {
        body: droppedFiles[0],
        method: 'PUT',
      });

      if (!s3Response.ok) {
        throw new Error('Unable to upload file.');
      }

      const payload: CreateDocumentUploadRequest['body'] = {
        comments: '',
        description: '',
        filename: checkFile?.name ?? 'Check Image',
        idRef: invoiceId,
        metadata: {},
        s3Url: getUrl,
        tableRef: 'invoice_payments',
      };

      return createDocumentUpload(payload);
    } catch (err) {
      setCheckFile(null);
      return setHasUploadError(true);
    }
  }, [createDocumentUploadData, hasUploadError]);

  return (
    <Form>
      <Box
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
        mb={2}
      >
        <CustomInput
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          fullWidth
          label="Amount"
          name="amountInCents"
          required
          type="number"
        />

        <CustomInput
          fullWidth
          label="Check Number"
          name="checkNumber"
          required
        />

        <CustomInput
          fullWidth
          label="Check Date"
          name="checkDate"
          required
          type="date"
        />
      </Box>

      <UploadDropzone
        helperText="Upload a check image"
        hideOnAcceptedFile
        onDrop={handleUploadFile}
        rejectedDropErrorMessage="Unable to upload file"
        showAcceptedFiles
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <SaveButton
          disabled={isSubmitting || !createDocumentUploadData?.data}
          isSaving={isSubmitting}
          type="submit"
        >
          Record Payment
        </SaveButton>
      </Box>
    </Form>
  );
};

export default InvoicePaymentFormFields;
